import * as Cesium from 'cesium';

/**
 * 智能实体管理器
 * 统一处理车辆、非机动车、行人的创建、更新和智能清理
 */
export class EntityManager {
  constructor(viewer) {
    this.viewer = viewer
    this.entities = new Map() // 存储所有实体
    this.lastMessageIds = new Set() // 上次消息中的ID集合

    // 实体统计
    this.stats = {
      vehicleCount: 0,
      bicycleCount: 0,
      pedestrianCount: 0,
      rsuCount: 0,
      reservedCount: 0,
      totalEntities: 0,
    }

    // 模型URL配置
    this.modelUrls = {
      vehicle: 'http://58.215.199.197:29002/roadmodal/zjp/su7.glb',
      bicycle: 'http://58.215.199.197:29002/roadmodal/zjp/diandonche.glb',
      pedestrian: 'http://58.215.199.197:29002/roadmodal/zjp/xingren.glb',
    }

    // 🎯 真实世界尺寸配置 - 根据实际车辆和人体尺寸调整
    this.realWorldDimensions = {
      1: {
        // 车辆 (小米SU7: 长4.97m, 宽1.96m, 高1.45m)
        length: 4.97,
        width: 1.96,
        height: 1.45,
        scale: 1.0, // 保持原始模型比例
        modelHeight: 1.45, // 假设模型原始高度
      },
      2: {
        // 非机动车 (电动车: 长1.8m, 宽0.6m, 高1.2m)
        length: 1.8,
        width: 0.6,
        height: 1.2,
        scale: 1.0,
        modelHeight: 1.2,
      },
      3: {
        // 行人 (标准成年人: 身高1.7m, 肩宽0.45m)
        length: 0.45,
        width: 0.3,
        height: 1.7,
        scale: 1.0,
        modelHeight: 1.7,
      },
    }

    // 方向偏移
    this.headingOffset = {
      1: 0,
      2: -90,
      3: -90,
    }
  }

  /**
   * 处理RSM参与者数据（主要入口）
   */
  processParticipants(participants, timestamp) {
    // console.log('🚀 处理参与者数据 ', participants ,timestamp)
    // 🕐 时间戳验证和修正
    const currentRealTime = Date.now()
    let processTime = timestamp

    // 检查时间戳是否合理（不能超过当前时间太多）
    if (timestamp > currentRealTime + 24 * 60 * 60 * 1000) {
      // 超过24小时
      console.warn(
        `⚠️ 检测到未来时间戳: ${new Date(
          timestamp
        ).toISOString()}, 使用当前时间替代`
      )
      processTime = currentRealTime
    }

    const currentTime = Cesium.JulianDate.fromDate(new Date(processTime))
    const currentMessageIds = new Set()

    // 处理当前消息中的所有参与者
    let validParticipantCount = 0
    let invalidParticipantCount = 0

    participants.forEach((participant, index) => {
      try {
        // 🔍 跳过无效的参与者数据
        if (!participant || typeof participant !== 'object') {
          //   console.warn(`⚠️ 跳过无效参与者数据 [${index}]:`, participant);
          invalidParticipantCount++
          return
        }

        const entityId = this.generateEntityId(participant)
        currentMessageIds.add(entityId)

        this.updateEntity(entityId, participant, currentTime)
        validParticipantCount++
      } catch (error) {
        console.error(`❌ 处理参与者 [${index}] 失败:`, error.message)
        console.error(`参与者数据:`, participant)
        invalidParticipantCount++
      }
    })

    // console.log(`📊 参与者处理统计: 有效=${validParticipantCount}, 无效=${invalidParticipantCount}, 总数=${participants.length}`);

    // 智能清理：删除不在当前消息中的实体
    this.cleanupMissingEntities(currentMessageIds)

    // 更新统计信息
    this.updateStats()

    // 如果是第一次有实体，飞到第一个实体位置
    // this.flyToFirstEntityIfNeeded(participants);

    // console.log(`📊 实体处理完成: 总数=${this.stats.totalEntities}, 车辆=${this.stats.vehicleCount}, 非机动车=${this.stats.bicycleCount}, 行人=${this.stats.pedestrianCount}, RSU=${this.stats.rsuCount}, 保留=${this.stats.reservedCount}`);
  }

  /**
   * 生成实体ID - 完全基于ptcId的唯一标识
   */
  generateEntityId(participant) {
    // 🔍 验证ptcId是否存在且有效
    if (
      participant.ptcId === null ||
      participant.ptcId === undefined ||
      participant.ptcId === ''
    ) {
      console.error(`❌ ptcId无效或为空:`, participant)
      throw new Error(
        `ptcId is required for entity identification. Received: ${participant.ptcId}`
      )
    }

    // 🔍 验证ptcType是否存在且有效
    if (participant.ptcType === null || participant.ptcType === undefined) {
      console.error(`❌ ptcType无效或为空:`, participant)
      throw new Error(
        `ptcType is required for entity identification. Received: ${participant.ptcType}`
      )
    }

    // 🎯 完全基于ptcId的唯一标识 - 忽略可能为null的id字段
    const entityId = `ptc_${participant.ptcId}_type_${participant.ptcType}`
    // console.log(`🔑 生成实体ID: ${entityId} (ptcId: ${participant.ptcId}, ptcType: ${participant.ptcType})`);

    return entityId
  }

  /**
   * 更新或创建实体
   */
  updateEntity(entityId, participant, currentTime) {
    // 🔍 验证坐标数据
    const lon = participant.longitude
    const lat = participant.latitude
    const elev = participant.elevation || 0

    if (isNaN(lon) || isNaN(lat) || !isFinite(lon) || !isFinite(lat)) {
      console.error(
        `❌ 无效坐标数据: 经度=${lon}, 纬度=${lat}, 实体=${entityId}`
      )
      return
    }

    // 检查坐标范围是否合理
    if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
      console.error(
        `❌ 坐标超出范围: 经度=${lon}, 纬度=${lat}, 实体=${entityId}`
      )
      return
    }

    // 🚨 检查height字段是否符合协议 [0..127]，分辨率5cm
    // if (participant.height !== undefined && participant.height !== null) {
    //   if (participant.height > 127) {
    //     console.error(`❌ height字段超出协议范围: ${participant.height} > 127, 实体=${entityId}`);
    //     console.error(`💡 建议修正：height应≤127 (对应≤6.35米)，当前${participant.height}对应${(participant.height * 5 / 100).toFixed(2)}米`);
    //     // 自动修正为最大值
    //     participant.height = 127;
    //   }
    // }

    // 🔍 检查ptcType是否符合协议 [0..5]
    if (participant.ptcType < 0 || participant.ptcType > 5) {
      //   console.error(`❌ ptcType超出协议范围: ${participant.ptcType}, 应为[0..5], 实体=${entityId}`);
      return
    }

    try {
      const position = Cesium.Cartesian3.fromDegrees(lon, lat, elev)

      // 🔍 验证转换结果
      if (
        !position ||
        isNaN(position.x) ||
        isNaN(position.y) ||
        isNaN(position.z)
      ) {
        // console.error(`❌ 坐标转换失败: 经度=${lon}, 纬度=${lat}, 结果=`, position);
        return
      }

      let entity = this.entities.get(entityId)

      if (!entity) {
        // 创建新实体
        entity = this.createEntity(entityId, participant)
        if (entity) {
          this.entities.set(entityId, entity)
          const typeName = this.getEntityTypeName(participant.ptcType)
          //   console.log(`✅ 创建实体: ${entityId} (${typeName}) 位置:(${lon.toFixed(6)}, ${lat.toFixed(6)})`);
        }
      }

      if (entity) {
        // 更新实体位置和方向
        this.updateEntityTransform(entity, participant, position, currentTime)
      }
    } catch (error) {
      console.error(`❌ 实体更新失败: ${entityId}`, error)
      console.error(`参与者数据:`, participant)
    }
  }

  /**
   * 创建实体
   */
  createEntity(entityId, participant) {
    const entityType = this.getEntityType(participant.ptcType)
    const entityTypeName = this.getEntityTypeName(participant.ptcType)

    // 创建位置属性
    const positionProperty = new Cesium.SampledPositionProperty()
    // 🛡️ 防闪烁：
    positionProperty.setInterpolationOptions({
      interpolationDegree: 3,
      interpolationAlgorithm: Cesium.HermitePolynomialApproximation,
    })

    // 🎯 设置前向和后向外推时间（防止数据断流时实体消失）
    positionProperty.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD
    positionProperty.forwardExtrapolationDuration = 2.0 // 2秒
    positionProperty.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD
    positionProperty.backwardExtrapolationDuration = 1.0 // 1秒
    // 创建方向属性（仅对有方向的实体）
    const orientationProperty =
      participant.heading !== undefined
        ? new Cesium.SampledProperty(Cesium.Quaternion)
        : null

    // 🛡️ 为方向插值设置与位置插值一致的选项，防止转向角错误
    if (orientationProperty) {
      orientationProperty.setInterpolationOptions({
        interpolationDegree: 3,
        interpolationAlgorithm: Cesium.HermitePolynomialApproximation,
      })

      // 🎯 设置方向的前向和后向外推时间（与位置保持一致）
      orientationProperty.forwardExtrapolationType =
        Cesium.ExtrapolationType.HOLD
      orientationProperty.forwardExtrapolationDuration = 2.0 // 2秒
      orientationProperty.backwardExtrapolationType =
        Cesium.ExtrapolationType.HOLD
      orientationProperty.backwardExtrapolationDuration = 1.0 // 1秒
    }

    try {
      // 🏗️ 根据ptcType创建不同类型的实体
      const entityConfig = {
        id: entityId,
        name: `${entityTypeName}-${participant.ptcId}`,
        position: positionProperty,
        label: {
          text: this.generateLabelText(participant, entityTypeName),
          font: this.getLabelFont(participant.ptcType),
          fillColor: this.getLabelColor(participant.ptcType),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          pixelOffset: this.getLabelOffset(participant.ptcType),
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          show: true,
        },
      }

      // 🎯 ptcType=4(RSU)和ptcType=5只显示标志点，不显示3D模型
      if (participant.ptcType === 4 || participant.ptcType === 5) {
        // RSU和保留类型：显示图标点而非3D模型
        entityConfig.point = {
          pixelSize: this.getPointSize(participant.ptcType),
          color: this.getPointColor(participant.ptcType),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          show: true,
        }

        // console.log(`🏗️ 创建RSU/特殊类型标志点: ${entityTypeName}`);
      } else {
        // 机动车、非机动车、行人：显示3D模型
        entityConfig.orientation = orientationProperty
        entityConfig.model = {
          uri: this.modelUrls[entityType],
          minimumPixelSize: this.getMinimumPixelSize(participant.ptcType),
          maximumScale: this.getMaximumScale(participant.ptcType),
          scale: this.getEntityScale(participant.ptcType),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          runAnimations: false,
        }
      }

      const entity = this.viewer.entities.add(entityConfig)

      // 添加自定义属性
      entity.participantType = participant.ptcType
      entity.lastUpdateTime = Date.now()

      return entity
    } catch (error) {
      console.error(`❌ 创建实体失败: ${entityId}`, error)
      return null
    }
  }

  /**
   * 更新实体位置和方向
   */
  updateEntityTransform(entity, participant, position, currentTime) {
    // 🔍 数据验证：检查坐标是否有效
    if (
      !position ||
      !Cesium.defined(position) ||
      isNaN(position.x) ||
      isNaN(position.y) ||
      isNaN(position.z)
    ) {
      console.warn(`⚠️ 无效位置数据: ${JSON.stringify(participant)}`)
      return
    }

    // 更新位置
    const positionProperty = entity.position
    positionProperty.addSample(currentTime, position)

    // 更新方向（如果有）- 添加安全检查
    // 🎯 ptcType=4,5只是点标记，不需要方向更新
    if (
      entity.orientation &&
      participant.heading !== undefined &&
      participant.ptcType !== 4 &&
      participant.ptcType !== 5
    ) {
      // 🔍 验证heading值
      const rawHeading = participant.heading
      if (
        isNaN(rawHeading) ||
        rawHeading === null ||
        rawHeading === undefined
      ) {
        console.warn(`⚠️ 无效航向角: ${rawHeading}`)
        return
      }

      try {
        const orientationProperty = entity.orientation

        // 🎯 标准化航向角到0-360度范围
        let normalizedHeading = rawHeading % 360
        if (normalizedHeading < 0) normalizedHeading += 360

        // 应用类型偏移
        const headingWithOffset =
          normalizedHeading + (this.headingOffset[participant.ptcType] || 0)
        const headingRadians = Cesium.Math.toRadians(headingWithOffset)

        // 🔍 验证弧度值
        if (isNaN(headingRadians) || !isFinite(headingRadians)) {
          console.warn(
            `⚠️ 无效弧度值: ${headingRadians}, 原始值: ${rawHeading}`
          )
          return
        }

        // 🛡️ 安全的四元数计算
        const hpr = new Cesium.HeadingPitchRoll(headingRadians, 0, 0)
        const orientation = Cesium.Transforms.headingPitchRollQuaternion(
          position,
          hpr
        )

        // 🔍 验证四元数结果
        if (
          !orientation ||
          isNaN(orientation.x) ||
          isNaN(orientation.y) ||
          isNaN(orientation.z) ||
          isNaN(orientation.w)
        ) {
          console.warn(`⚠️ 无效四元数结果:`, orientation)
          return
        }

        // 🛡️ 手动归一化四元数（防止异常）
        const length = Math.sqrt(
          orientation.x * orientation.x +
            orientation.y * orientation.y +
            orientation.z * orientation.z +
            orientation.w * orientation.w
        )

        if (length === 0 || isNaN(length)) {
          console.warn(`⚠️ 四元数长度为零或NaN: ${length}`)
          return
        }

        const normalizedOrientation = new Cesium.Quaternion(
          orientation.x / length,
          orientation.y / length,
          orientation.z / length,
          orientation.w / length
        )

        orientationProperty.addSample(currentTime, normalizedOrientation)
      } catch (error) {
        console.error(`❌ 方向计算失败: ${entity.id}`, error)
        console.error(
          `数据: heading=${rawHeading}, ptcType=${participant.ptcType}`
        )
      }
    }

    // 更新标签
    if (entity.label && entity.label.text) {
      const entityTypeName = this.getEntityTypeName(participant.ptcType)
      entity.label.text = this.generateLabelText(participant, entityTypeName)
    }

    // 更新时间戳
    entity.lastUpdateTime = Date.now()
  }

  /**
   * 智能清理：删除不在当前消息中的实体 - 完全基于ptcId标识
   */
  cleanupMissingEntities(currentMessageIds) {
    const entitiesToRemove = []

    // 🔍 找出需要清理的实体（不在当前ptcId集合中）
    this.entities.forEach((entity, entityId) => {
      if (!currentMessageIds.has(entityId)) {
        // 提取实体的ptcId用于日志
        const ptcInfo = entity.name ? entity.name.match(/(\w+)-(\d+)/) : null
        // console.log('ptcInfo', ptcInfo)
        const ptcId = ptcInfo ? ptcInfo[2] : 'unknown'
        const ptcType = ptcInfo ? ptcInfo[1] : 'unknown'

        entitiesToRemove.push({
          entityId,
          ptcId,
          ptcType,
          entity,
        })
      }
    })

    // 🗑️ 执行清理操作
    entitiesToRemove.forEach(({ entityId, ptcId, ptcType, entity }) => {
      try {
        this.viewer.entities.remove(entity)
        this.entities.delete(entityId)
        // console.log(`🗑️ 清理已离线设备: ptcId=${ptcId}, type=${ptcType}, entityId=${entityId}`);
      } catch (error) {
        // console.error(`❌ 清理实体失败: ptcId=${ptcId}, entityId=${entityId}`, error);
        // 即使删除失败，也要从Map中移除，避免内存泄漏
        this.entities.delete(entityId)
      }
    })

    if (entitiesToRemove.length > 0) {
      //   console.log(`🧹 设备清理完成: 清理了${entitiesToRemove.length}个离线设备`);
      // 按类型统计清理的设备
      const cleanupStats = entitiesToRemove.reduce((stats, { ptcType }) => {
        stats[ptcType] = (stats[ptcType] || 0) + 1
        return stats
      }, {})
      console.log(`📊 清理统计:`, entitiesToRemove)
    }
  }

  /**
   * 更新统计信息
   */
  updateStats() {
    let vehicleCount = 0
    let bicycleCount = 0
    let pedestrianCount = 0
    let rsuCount = 0
    let reservedCount = 0

    this.entities.forEach(entity => {
      switch (entity.participantType) {
        case 1:
          vehicleCount++
          break
        case 2:
          bicycleCount++
          break
        case 3:
          pedestrianCount++
          break
        case 4:
          rsuCount++
          break
        case 5:
          reservedCount++
          break
      }
    })

    this.stats = {
      vehicleCount,
      bicycleCount,
      pedestrianCount,
      rsuCount,
      reservedCount,
      totalEntities: this.entities.size,
    }
  }

  /**
   * 如果是第一次有实体，飞到第一个实体位置
   */
  flyToFirstEntityIfNeeded(participants) {
    if (this.entities.size === 1 && participants.length > 0) {
      const firstParticipant = participants[0]
      const flyToPosition = Cesium.Cartesian3.fromDegrees(
        firstParticipant.longitude,
        firstParticipant.latitude,
        350 // 350米高度
      )

      this.viewer.camera.flyTo({
        destination: flyToPosition,
        orientation: {
          heading: Cesium.Math.toRadians(0),
          pitch: Cesium.Math.toRadians(-60),
          roll: 0,
        },
        duration: 3,
        complete: () => {
          console.log(
            `🎯 相机已飞到第一个实体位置: (${firstParticipant.longitude}, ${firstParticipant.latitude})`
          )
        },
      })
    }
  }

  /**
   * 清除所有实体
   */
  clearAllEntities() {
    console.log(`🗑️ 清除所有实体，当前数量: ${this.entities.size}`)

    this.entities.forEach(entity => {
      try {
        this.viewer.entities.remove(entity)
      } catch (error) {
        console.error(`❌ 删除实体失败:`, error)
      }
    })

    this.entities.clear()
    this.updateStats()
    console.log('✅ 所有实体已清除')
  }

  /**
   * 获取统计信息
   */
  getStats() {
    return { ...this.stats }
  }

  /**
   * 获取基于ptcId的设备详细信息 - 用于调试和监控
   */
  getDeviceInfo() {
    const devices = []

    this.entities.forEach((entity, entityId) => {
      // 从entity名称中提取ptcId和类型信息
      const ptcInfo = entity.name ? entity.name.match(/(\w+)-(\d+)/) : null
      const ptcId = ptcInfo ? ptcInfo[2] : 'unknown'
      const typeName = ptcInfo ? ptcInfo[1] : 'unknown'

      devices.push({
        entityId,
        ptcId,
        ptcType: entity.participantType,
        typeName,
        lastUpdate: entity.lastUpdateTime,
        age: Date.now() - (entity.lastUpdateTime || 0),
      })
    })

    return {
      devices,
      totalCount: devices.length,
      devicesByType: devices.reduce((acc, device) => {
        acc[device.typeName] = (acc[device.typeName] || 0) + 1
        return acc
      }, {}),
      oldestDevice:
        devices.length > 0 ? Math.max(...devices.map(d => d.age)) : 0,
    }
  }

  /**
   * 检查是否存在指定ptcId的设备
   */
  hasDevice(ptcId, ptcType) {
    const expectedEntityId = `ptc_${ptcId}_type_${ptcType}`
    return this.entities.has(expectedEntityId)
  }

  /**
   * 根据ptcId获取设备实体
   */
  getDeviceByPtcId(ptcId, ptcType) {
    const expectedEntityId = `ptc_${ptcId}_type_${ptcType}`
    return this.entities.get(expectedEntityId)
  }

  // ========== 辅助方法 ==========

  getEntityType(ptcType) {
    switch (ptcType) {
      case 1:
        return 'vehicle'
      case 2:
        return 'bicycle'
      case 3:
        return 'pedestrian'
      case 4:
        return 'rsu'
      case 5:
        return 'reserved'
      default:
        return 'vehicle'
    }
  }

  getEntityTypeName(ptcType) {
    switch (ptcType) {
      case 1:
        return '车辆'
      case 2:
        return '非机动车'
      case 3:
        return '行人'
      case 4:
        return 'RSU'
      case 5:
        return '保留'
      default:
        return '未知'
    }
  }

  /**
   * 🎯 获取实体缩放比例 - 基于真实世界尺寸
   * 确保在任何视角下都显示合理的大小
   */
  getEntityScale(ptcType) {
    const dimensions = this.realWorldDimensions[ptcType]
    if (!dimensions) {
      console.warn(`未找到类型 ${ptcType} 的尺寸配置，使用默认值`)
      return 1.0
    }

    // 🎯 根据不同类型返回适合的缩放比例
    switch (ptcType) {
      case 1: // 车辆 - 小米SU7，保持真实比例
        return 1.0
      case 2: // 非机动车 - 电动车，适当缩放
        return 1.0
      case 3: // 行人 - 确保1.7米身高
        return 1.0
      default:
        return 1.0
    }
  }

  /**
   * 🔍 获取最小像素尺寸 - 确保远距离可见性
   */
  getMinimumPixelSize(ptcType) {
    switch (ptcType) {
      case 1:
        return 20 // 车辆 - 稍大保证可见性
      case 2:
        return 15 // 非机动车 - 中等大小
      case 3:
        return 10 // 行人 - 稍小但清晰可见
      default:
        return 20
    }
  }

  /**
   * 📏 获取最大缩放比例 - 防止近距离过大
   */
  getMaximumScale(ptcType) {
    switch (ptcType) {
      case 1:
        return 15000 // 车辆 - 相对较小的最大缩放
      case 2:
        return 12000 // 非机动车 - 中等最大缩放
      case 3:
        return 10000 // 行人 - 较小的最大缩放
      default:
        return 20000
    }
  }

  /**
   * 📍 获取点标记大小 - 用于RSU等非3D模型实体
   */
  getPointSize(ptcType) {
    switch (ptcType) {
      case 4:
        return 20 // RSU - 中等大小的点
      case 5:
        return 15 // 保留类型 - 较小的点
      default:
        return 15
    }
  }

  /**
   * 🎨 获取点标记颜色 - 用于RSU等非3D模型实体
   */
  getPointColor(ptcType) {
    switch (ptcType) {
      case 4:
        return Cesium.Color.ORANGE // RSU - 橙色表示路侧单元
      case 5:
        return Cesium.Color.GRAY // 保留类型 - 灰色
      default:
        return Cesium.Color.RED
    }
  }

  getLabelFont(ptcType) {
    switch (ptcType) {
      case 1:
        return '12px Arial'
      case 2:
        return '11px Arial'
      case 3:
        return '10px Arial'
      case 4:
        return '12px Arial' // RSU
      case 5:
        return '10px Arial' // 保留类型
      default:
        return '12px Arial'
    }
  }

  getLabelColor(ptcType) {
    switch (ptcType) {
      case 1:
        return Cesium.Color.WHITE
      case 2:
        return Cesium.Color.GREEN
      case 3:
        return Cesium.Color.YELLOW
      case 4:
        return Cesium.Color.ORANGE // RSU
      case 5:
        return Cesium.Color.GRAY // 保留类型
      default:
        return Cesium.Color.WHITE
    }
  }

  getLabelOffset(ptcType) {
    switch (ptcType) {
      case 1:
        return new Cesium.Cartesian2(0, -50)
      case 2:
        return new Cesium.Cartesian2(0, -40)
      case 3:
        return new Cesium.Cartesian2(0, -30)
      case 4:
        return new Cesium.Cartesian2(0, -35) // RSU
      case 5:
        return new Cesium.Cartesian2(0, -25) // 保留类型
      default:
        return new Cesium.Cartesian2(0, -50)
    }
  }

  generateLabelText(participant, entityTypeName) {
    const dimensions = this.realWorldDimensions[participant.ptcType]
    const speedText =
      participant.speed !== undefined
        ? `\n速度: ${participant.speed.toFixed(1)} m/s`
        : ''
    const headingText =
      participant.heading !== undefined
        ? `\n方向: ${participant.heading.toFixed(1)}°`
        : ''

    // 对于RSU和保留类型，不显示尺寸信息
    let sizeText = ''
    if (participant.ptcType <= 3 && dimensions) {
      sizeText = `\n尺寸: ${dimensions.length}x${dimensions.width}x${dimensions.height}m`
    }

    return `${entityTypeName}-${participant.ptcId}${speedText}${headingText}${sizeText}`
  }
}
